/**
 * @fileoverview Live Dashboard page for interactive monitoring display
 * 
 * A full-screen capable dashboard designed for separate monitor displays
 * with real-time data visualization and interactive components.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  ArrowsPointingOutIcon,
  Cog6ToothIcon,
  MicrophoneIcon,
  XMarkIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  PlayIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { useWebSocket } from '../hooks/useWebSocket';
import { useVoiceInput } from '../hooks/useVoiceInput';
import { useTextToSpeech } from '../hooks/useTextToSpeech';
import { useAudioStreaming } from '../hooks/useAudioStreaming';
import VoiceWaveform from '../components/VoiceWaveform';
import DashboardGrid, { DEFAULT_LAYOUT } from '../components/DashboardGrid';
import DashboardWidget from '../components/DashboardWidget';
import { ResponsiveContainer, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Area, AreaChart, ComposedChart, Line } from 'recharts';
import { parseCommand } from '../api/commands';
import { executeIntent } from '../api/intentExecutor';

function LiveDashboardPage() {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showSettings, setShowSettings] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [debugMessages, setDebugMessages] = useState([]);
  const dashboardRef = useRef(null);

  // Grid layout state
  const [gridLayout, setGridLayout] = useState(DEFAULT_LAYOUT);
  const [isGridEditing, setIsGridEditing] = useState(false);
  const [expandedWidgetId, setExpandedWidgetId] = useState(null);

  // WebSocket connection with auto-connect for Live Dashboard
  const { connectionStatus, isConnected, lastMessage, toggleConnection, shouldConnect, sendMessage, websocketId } = useWebSocket(null, true, 'dashboard');

  // Voice input for commands
  const {
    isSupported: isVoiceSupported,
    isListening,
    isProcessing,
    transcript,
    interimTranscript,
    displayTranscript,
    error: voiceError,
    confidence,
    continuousMode,
    isActivelySpeaking,
    isWaitingForMoreSpeech,
    wakeWord,
    isWaitingForWakeWord,
    wakeWordActiveUntil,
    startListening,
    stopListening,
    cancelListening,
    resetTranscript,
    resetTranscriptOnly,
    isActive: isVoiceActive,
    hasResult,
    hasError: hasVoiceError
  } = useVoiceInput({
    continuousSilenceTimeout: 1500, // 1.5 seconds pause before sending command
    singleSilenceTimeout: 1500 // 1.5 seconds for single commands
  });

  // Text-to-speech for reading responses
  const {
    isSupported: isTTSSupported,
    isPollySupported,
    isSpeaking,
    speakResponse,
    speak: speakText,
    stop: stopSpeaking,
    isActive: isTTSActive,
    provider: ttsProvider,
    usingPolly,
    setUsingPolly,
    pollyVoices,
    voices: webSpeechVoices,
    currentVoice,
    setCurrentVoice,
    rate,
    setRate,
    volume,
    setVolume
  } = useTextToSpeech({
    defaultRate: 1.1,
    defaultPitch: 1.0,
    defaultVolume: 0.8
  });

  // Audio streaming for real-time TTS
  const {
    activeStreams,
    isPlaying: isStreamPlaying,
    currentStreamId,
    error: streamError,
    handleStreamMessage,
    stopStream,
    stopAllStreams,
    testStreaming,
    sendAnnouncement,
    streamResponse,
    hasActiveStreams,
    currentPlaybackText // Text currently being spoken (for echo cancellation)
  } = useAudioStreaming();

  // Voice command state
  const [voiceCommandResult, setVoiceCommandResult] = useState(null);
  const [executionResult, setExecutionResult] = useState(null);
  const [isProcessingCommand, setIsProcessingCommand] = useState(false);
  const [delayedIsListening, setDelayedIsListening] = useState(false);
  const [finalResults, setFinalResults] = useState([]); // Array of final speech segments
  const [displayText, setDisplayText] = useState(''); // What to show in UI
  const [lastProcessedTranscript, setLastProcessedTranscript] = useState('');

  // Current context for intent execution
  const [currentItemId, setCurrentItemId] = useState(null);
  const [currentIssueNumber, setCurrentIssueNumber] = useState(null);
  // Voice preferences
  const [voicePreference, setVoicePreference] = useState({
    id: 'Amy',
    engine: 'generative',
    speed: '1.0' // Default to normal speed (1.0x)
  });

  // Chat state
  const [showChat, setShowChat] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [isChatProcessing, setIsChatProcessing] = useState(false);
  const chatTextareaRef = useRef(null);

  // Audio state
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [showAudioSettings, setShowAudioSettings] = useState(false);

  // Log websocketId changes for debugging
  useEffect(() => {
    if (websocketId) {
      // console.log('🆔 Dashboard WebSocket ID:', websocketId);
    }
  }, [websocketId]);

  // Timer effect for wake word countdown
  useEffect(() => {
    if (wakeWordActiveUntil && Date.now() < wakeWordActiveUntil) {
      const interval = setInterval(() => {
        // Force re-render every second to update countdown
        setCurrentTime(new Date());
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [wakeWordActiveUntil]);

  // Add delay to voice button icon changes to prevent flicker during reconnections
  useEffect(() => {
    if (isListening) {
      // Immediately show listening state when starting
      setDelayedIsListening(true);
    } else {
      // If continuous mode is disabled, it was a manual stop - change immediately
      if (!continuousMode) {
        setDelayedIsListening(false);
      } else {
        // Add 1 second delay when stopping automatically to allow for reconnection
        const timeout = setTimeout(() => {
          setDelayedIsListening(false);
        }, 1000);
        return () => clearTimeout(timeout);
      }
    }
  }, [isListening, continuousMode]);

  // Capture each final result from the browser
  useEffect(() => {
    if (transcript && transcript !== lastProcessedTranscript && transcript.trim()) {
      if (debugMode) console.log('📝 New final result:', transcript);
      setLastProcessedTranscript(transcript);
      setFinalResults(prev => [...prev, transcript.trim()]);
    }
  }, [transcript, lastProcessedTranscript]);

  // Update display text to show accumulated final results + current interim
  useEffect(() => {
    const finalText = finalResults.join(' ');
    if (interimTranscript && interimTranscript.trim()) {
      // Show final results + current interim
      setDisplayText(finalText ? `${finalText} ${interimTranscript}` : interimTranscript);
    } else if (finalText) {
      // Show just the final results
      setDisplayText(finalText);
    } else {
      setDisplayText('');
    }
  }, [finalResults, interimTranscript]);

  // Clear everything when voice mode completely stops
  useEffect(() => {
    if (!isListening && !isProcessing && !interimTranscript && !transcript) {
      setFinalResults([]);
      setDisplayText('');
      setLastProcessedTranscript('');
    }
  }, [isListening, isProcessing, interimTranscript, transcript]);

  // Map widget names to IDs for voice commands
  const WIDGET_NAME_MAP = {
    // Main canvas / chart variations
    'graph': 'mainCanvas',
    'chart': 'mainCanvas',
    'bar chart': 'mainCanvas',
    'content': 'mainCanvas',
    'content activity': 'mainCanvas',
    'weekly content': 'mainCanvas',
    'main': 'mainCanvas',
    'main canvas': 'mainCanvas',
    // Time widget variations
    'time': 'timeWidget',
    'clock': 'timeWidget',
    'trend': 'timeWidget',
    'trends': 'timeWidget',
    // Stats widget variations
    'stats': 'statsGrid',
    'metrics': 'statsGrid',
    'statistics': 'statsGrid',
    // Side panel variations
    'status': 'sidePanel',
    'system': 'sidePanel',
    'system status': 'sidePanel',
    'side': 'sidePanel',
    'side panel': 'sidePanel',
    'activity': 'sidePanel'
  };

  // Resolve widget name to ID
  const resolveWidgetId = useCallback((widgetId, widgetName) => {
    // If widgetId is provided and valid, use it
    if (widgetId && gridLayout[widgetId]) {
      return widgetId;
    }
    // Try to match widget name
    if (widgetName) {
      const normalizedName = widgetName.toLowerCase().trim();
      if (WIDGET_NAME_MAP[normalizedName]) {
        return WIDGET_NAME_MAP[normalizedName];
      }
      // Try partial matching
      for (const [name, id] of Object.entries(WIDGET_NAME_MAP)) {
        if (normalizedName.includes(name) || name.includes(normalizedName)) {
          return id;
        }
      }
    }
    // Default to mainCanvas if nothing matches (most likely target for "expand the graph")
    return 'mainCanvas';
  }, [gridLayout]);

  // Handle widget expansion
  const handleExpandWidget = useCallback((widgetId, widgetName) => {
    const resolvedId = resolveWidgetId(widgetId, widgetName);
    if (debugMode) console.log('🔍 Expanding widget:', { widgetId, widgetName, resolvedId });
    setExpandedWidgetId(resolvedId);
  }, [resolveWidgetId, debugMode]);

  // Handle widget collapse
  const handleCollapseWidget = useCallback(() => {
    if (debugMode) console.log('🔍 Collapsing widget');
    setExpandedWidgetId(null);
  }, [debugMode]);

  // Handle incoming layout commands via WebSocket
  const handleLayoutCommand = useCallback((command) => {
    // Supports commands like: { widgetId, colStart, colSpan, rowStart, rowSpan }
    if (command.widgetId && gridLayout[command.widgetId]) {
      setGridLayout(prev => ({
        ...prev,
        [command.widgetId]: {
          ...prev[command.widgetId],
          ...(command.colStart !== undefined && { colStart: command.colStart }),
          ...(command.colSpan !== undefined && { colSpan: command.colSpan }),
          ...(command.rowStart !== undefined && { rowStart: command.rowStart }),
          ...(command.rowSpan !== undefined && { rowSpan: command.rowSpan })
        }
      }));
    }
  }, [gridLayout]);

  // Monitor connection status and attempt reconnection
  useEffect(() => {
    let reconnectInterval;
    
    if (!isConnected && shouldConnect) {
      // Set up reconnection interval when disconnected
      reconnectInterval = setInterval(() => {
        // console.log('Live Dashboard: Attempting WebSocket reconnection...');
        if (!isConnected && !shouldConnect) {
          toggleConnection(); // This will set shouldConnect to true
        }
      }, 5000);
    }

    return () => {
      if (reconnectInterval) {
        clearInterval(reconnectInterval);
      }
    };
  }, [isConnected, shouldConnect, toggleConnection]);

  // Handle WebSocket messages
  useEffect(() => {
    if (lastMessage) {
      // console.log('🔍 Live Dashboard WebSocket message received:', {
      //   type: lastMessage.type,
      //   hasCommand: !!lastMessage.command,
      //   fullMessage: lastMessage
      // });

      // Add to debug messages if debug mode is enabled
      if (debugMode) {
        setDebugMessages(prev => {
          const newMessage = {
            id: Date.now(),
            timestamp: new Date().toISOString(),
            data: lastMessage
          };
          // Keep only last 50 messages
          return [newMessage, ...prev.slice(0, 49)];
        });
      }

      // Handle layout commands (for WebSocket-based layout changes)
      if (lastMessage.type === 'layout_command' && lastMessage.command) {
        handleLayoutCommand(lastMessage.command);
      }

      // Handle audio streaming messages
      if (lastMessage.type && lastMessage.type.startsWith('audio_')) {
        // console.log('🎵 Handling audio stream message:', lastMessage.type);
        handleStreamMessage(lastMessage);
      }

      // Dashboard ID is now handled automatically by the useWebSocket hook

      // Handle other dashboard-specific messages here
      // e.g., real-time stats updates, alerts, etc.
    }
  }, [lastMessage, debugMode, handleStreamMessage, handleLayoutCommand]);

  // Dashboard channel subscription is now handled automatically by the useWebSocket hook

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Handle full screen changes
  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullScreenChange);
  }, []);

  const toggleFullScreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await dashboardRef.current?.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error('Error toggling fullscreen:', error);
    }
  };

  // Handle navigation from intent execution
  const handleIntentNavigate = useCallback(({ type, id, issueNumber }) => {
    if (debugMode) console.log('🧭 Intent navigation:', { type, id, issueNumber });
    if (type === 'item') {
      setCurrentItemId(id);
    } else if (type === 'issue') {
      setCurrentIssueNumber(issueNumber || id);
    }
  }, []);

  // Handle voice command processing
  const handleVoiceCommand = useCallback(async (commandText) => {
    if (!commandText.trim()) return;

    setIsProcessingCommand(true);
    setVoiceCommandResult(null);
    setExecutionResult(null);

    try {
      if (debugMode) {
        console.log('🎤 Processing voice command:', commandText);
        console.log('🔍 DEBUG: Frontend websocketId:', websocketId, 'isConnected:', isConnected);
      }

      const result = await parseCommand(commandText, {
        context: {
          source: 'live-dashboard',
          websocketId,
          currentItemId,
          currentIssueNumber,
          userId: 'dashboard-user', // You might want to get this from auth context
          voice: voicePreference
        }
      });

      if (debugMode) console.log('✅ Voice command parsed:', result);
      setVoiceCommandResult(result);

      // Execute the parsed intent if it's not an error or unknown
      if (result.intent && result.intent !== 'unknown' && !result.error) {
        if (debugMode) console.log('🚀 Executing intent:', result.intent, 'with params:', result.parameters);

        // Handle UI control intents locally
        if (result.intent === 'expand_widget') {
          handleExpandWidget(result.parameters?.widgetId, result.parameters?.widgetName);
          setExecutionResult({
            success: true,
            action: 'expand_widget',
            message: 'Widget expanded'
          });
        } else if (result.intent === 'collapse_widget') {
          handleCollapseWidget();
          setExecutionResult({
            success: true,
            action: 'collapse_widget',
            message: 'Widget collapsed'
          });
        } else {
          // Execute other intents via the intent executor
          const execResult = await executeIntent(result, {
            currentItemId,
            currentIssueNumber,
            onNavigate: handleIntentNavigate,
            onItemsUpdate: () => {
              if (debugMode) console.log('📋 Items updated - would trigger refresh');
            },
            onIssuesUpdate: () => {
              if (debugMode) console.log('📰 Issues updated - would trigger refresh');
            },
          });

          if (debugMode) console.log('✅ Intent execution result:', execResult);
          setExecutionResult(execResult);

          // Update current item if navigation occurred
          if (execResult.action === 'navigate_to_item' && execResult.data) {
            setCurrentItemId(execResult.data.hash || execResult.data.id);
          }
        }
      }

      // Auto-clear result after 5 seconds
      setTimeout(() => {
        setVoiceCommandResult(null);
        setExecutionResult(null);
      }, 5000);

    } catch (error) {
      console.error('❌ Voice command error:', error);
      const errorResult = {
        error: 'Failed to process voice command',
        details: error.message
      };
      setVoiceCommandResult(errorResult);

      // Note: Backend now handles streaming error messages via WebSocket

      // Auto-clear error after 5 seconds
      setTimeout(() => {
        setVoiceCommandResult(null);
        setExecutionResult(null);
      }, 5000);
    } finally {
      setIsProcessingCommand(false);
    }
  }, [websocketId, currentItemId, currentIssueNumber, voicePreference, handleIntentNavigate, handleExpandWidget, handleCollapseWidget, debugMode]);

  // Helper function to check if recognized speech matches playback text (echo detection)
  const isEchoOfPlayback = useCallback((recognizedText, playbackText) => {
    if (!playbackText || !recognizedText) return false;

    const recognized = recognizedText.toLowerCase().trim();
    const playback = playbackText.toLowerCase().trim();

    // Check if recognized text is a substring of playback (mic picked up part of response)
    if (playback.includes(recognized) && recognized.length > 3) {
      return true;
    }

    // Check word overlap - if more than 50% of recognized words appear in playback
    const recognizedWords = recognized.split(/\s+/).filter(w => w.length > 2);
    const playbackWords = new Set(playback.split(/\s+/).filter(w => w.length > 2));

    if (recognizedWords.length === 0) return false;

    const matchingWords = recognizedWords.filter(word => playbackWords.has(word));
    const overlapRatio = matchingWords.length / recognizedWords.length;

    if (debugMode && overlapRatio > 0.3) {
      console.log('🔊 Echo detection:', {
        recognized,
        matchingWords,
        overlapRatio: (overlapRatio * 100).toFixed(0) + '%'
      });
    }

    return overlapRatio > 0.5; // More than 50% word match = likely echo
  }, [debugMode]);

  // Handle voice input completion
  useEffect(() => {
    if (hasResult && transcript && !isProcessingCommand && finalResults.length > 0) {
      // Use all accumulated final results
      const completeCommand = finalResults.join(' ');
      const commandLower = completeCommand.toLowerCase().trim();

      // Check if audio is playing (TTS or streaming)
      const isAudioPlaying = isSpeaking || hasActiveStreams;

      // Check for stop commands first (handle locally, never send to backend)
      const isStopCommand = /^(stop|cancel|quiet|silence|shut up|be quiet|hush)$/i.test(commandLower);

      if (isStopCommand) {
        if (isAudioPlaying) {
          if (debugMode) console.log('🛑 Stop command during audio playback:', completeCommand);
          // Stop all audio
          if (isSpeaking) stopSpeaking();
          if (hasActiveStreams) stopAllStreams();

          setVoiceCommandResult({
            intent: 'stop_audio',
            confidence: 1.0,
            explanation: 'Audio stopped'
          });
        } else {
          if (debugMode) console.log('🛑 Stop command (no audio playing):', completeCommand);
          setVoiceCommandResult({
            intent: 'stop_audio',
            confidence: 1.0,
            explanation: 'No audio playing'
          });
        }

        // Auto-clear after 2 seconds
        setTimeout(() => {
          setVoiceCommandResult(null);
        }, 2000);

        // Clear and reset immediately
        setFinalResults([]);
        setDisplayText('');
        setLastProcessedTranscript('');
        resetTranscriptOnly();
        return;
      }

      // If audio is playing, check for echo
      if (isAudioPlaying && currentPlaybackText) {
        if (isEchoOfPlayback(completeCommand, currentPlaybackText)) {
          if (debugMode) console.log('🔊 Detected echo of playback, ignoring:', completeCommand);
          // Clear and reset immediately
          setFinalResults([]);
          setDisplayText('');
          setLastProcessedTranscript('');
          resetTranscriptOnly();
          return;
        }
      }

      // Process the command (whether audio is playing or not)
      if (debugMode) console.log('🎯 Sending complete command:', completeCommand);

      // Clear states BEFORE processing to prevent re-entry
      setFinalResults([]);
      setDisplayText('');
      setLastProcessedTranscript('');

      // Reset transcript immediately to prevent re-triggering
      resetTranscriptOnly();

      // Now process the command
      handleVoiceCommand(completeCommand);
    }
  }, [hasResult, transcript, isProcessingCommand, finalResults, handleVoiceCommand, resetTranscriptOnly, isSpeaking, hasActiveStreams, stopSpeaking, stopAllStreams, debugMode, currentPlaybackText, isEchoOfPlayback]);

  // Handle voice input button click
  const handleVoiceButtonClick = () => {
    // Close other popups when starting voice input
    setShowAudioSettings(false);
    setShowChat(false);

    if (isVoiceActive) {
      cancelListening();
    } else if (isVoiceSupported) {
      startListening();
    }
  };

  // Handle chat message submission
  const handleChatSubmit = useCallback(async (e) => {
    e?.preventDefault();

    if (!chatMessage.trim() || isChatProcessing) return;

    // Clear input immediately and show processing state
    const messageToSend = chatMessage;
    setChatMessage('');
    setIsChatProcessing(true);

    try {
      if (debugMode) {
        console.log('💬 Submitting chat message:', messageToSend);
        console.log('🔍 DEBUG: Chat websocketId:', websocketId, 'isConnected:', isConnected);
      }

      const result = await parseCommand(messageToSend, {
        context: {
          source: 'live-dashboard-chat',
          websocketId,
          userId: 'dashboard-user',
          voice: voicePreference
        }
      });

      if (debugMode) console.log('✅ Chat message processed:', result);

    } catch (error) {
      console.error('❌ Chat message error:', error);
    } finally {
      setIsChatProcessing(false);
    }
  }, [chatMessage, websocketId, isConnected, voicePreference, isChatProcessing]);

  // Handle chat textarea auto-resize
  useEffect(() => {
    const textarea = chatTextareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    }
  }, [chatMessage]);

  // Handle Enter key in chat
  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleChatSubmit(e);
    }
  };

  // Handle audio button click
  const handleAudioButtonClick = () => {
    if (isAudioMuted) {
      // If muted, unmute first
      setIsAudioMuted(false);
      setShowAudioSettings(false);
    } else {
      // If not muted, toggle audio settings
      setShowAudioSettings(!showAudioSettings);
      // Close other popups when opening audio settings
      if (!showAudioSettings) {
        setShowChat(false);
        }
    }
  };

  // Handle explicit mute action (for settings popup)
  const handleAudioMute = () => {
    setIsAudioMuted(true);
    setShowAudioSettings(false);

    // Stop any current TTS
    if (isSpeaking) {
      stopSpeaking();
    }

    // Stop any active streams
    if (hasActiveStreams) {
      stopAllStreams();
    }
  };

  // Override TTS and streaming functions to check muted state
  const mutedAwareSpeakText = useCallback((...args) => {
    if (!isAudioMuted) {
      return speakText(...args);
    }
  }, [isAudioMuted, speakText]);

  const mutedAwareStreamResponse = useCallback((sendMessage, text, options = {}) => {
    if (!isAudioMuted) {
      return streamResponse(sendMessage, text, options);
    }
  }, [isAudioMuted, streamResponse]);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Chart data
  const weeklyData = [
    { day: 'Mon', articles: 28, videos: 12, podcasts: 5, engagement: 65 },
    { day: 'Tue', articles: 42, videos: 18, podcasts: 8, engagement: 78 },
    { day: 'Wed', articles: 20, videos: 8, podcasts: 4, engagement: 42 },
    { day: 'Thu', articles: 55, videos: 24, podcasts: 10, engagement: 95 },
    { day: 'Fri', articles: 48, videos: 20, podcasts: 8, engagement: 88 },
    { day: 'Sat', articles: 15, videos: 6, podcasts: 2, engagement: 35 },
    { day: 'Sun', articles: 32, videos: 16, podcasts: 6, engagement: 72 }
  ];

  const newsletterMetrics = [
    {
      name: 'New Articles',
      value: 147,
      color: '#175677',
      trend: [
        { day: 1, value: 120 },
        { day: 2, value: 132 },
        { day: 3, value: 128 },
        { day: 4, value: 141 },
        { day: 5, value: 135 },
        { day: 6, value: 139 },
        { day: 7, value: 147 }
      ]
    },
    {
      name: 'New Subscribers',
      value: 1284,
      color: '#cbe5f2',
      trend: [
        { day: 1, value: 1180 },
        { day: 2, value: 1201 },
        { day: 3, value: 1195 },
        { day: 4, value: 1223 },
        { day: 5, value: 1245 },
        { day: 6, value: 1267 },
        { day: 7, value: 1284 }
      ]
    },
    {
      name: 'Opens (last issue)',
      value: '68.5%',
      color: '#d60a3c',
      trend: [
        { day: 1, value: 65.2 },
        { day: 2, value: 66.8 },
        { day: 3, value: 64.1 },
        { day: 4, value: 67.3 },
        { day: 5, value: 69.1 },
        { day: 6, value: 67.8 },
        { day: 7, value: 68.5 }
      ]
    },
    {
      name: 'Engagement (last issue)',
      value: '12.3%',
      color: '#8B5CF6',
      trend: [
        { day: 1, value: 11.2 },
        { day: 2, value: 11.8 },
        { day: 3, value: 10.9 },
        { day: 4, value: 12.1 },
        { day: 5, value: 12.7 },
        { day: 6, value: 12.0 },
        { day: 7, value: 12.3 }
      ]
    }
  ];

  const trendData = [
    { day: 'Week 1', value: 287 },
    { day: 'Week 2', value: 342 },
    { day: 'Week 3', value: 298 },
    { day: 'Week 4', value: 387 },
    { day: 'Week 5', value: 445 },
    { day: 'Week 6', value: 521 },
    { day: 'Week 7', value: 489 }
  ];

  return (
    <div 
      ref={dashboardRef}
      className={`
        relative transition-all duration-300 text-white z-50
        ${isFullScreen 
          ? 'fixed inset-0 bg-gradient-to-br from-gray-900 via-obn-blue to-gray-800' 
          : 'min-h-screen bg-gradient-to-br from-gray-900 via-obn-blue to-gray-800'
        }
      `}
    >
      {/* Header Controls */}
      <div className="absolute top-0 left-0 right-0 z-10 flex justify-between items-center p-6">
        <div className="flex items-center pl-2">
          <img 
            src="/off-by-none-logo.svg" 
            alt="Off-by-none" 
            className={`w-auto max-w-none transition-all duration-300 ${
              isFullScreen 
                ? 'h-14 sm:h-16 md:h-18 lg:h-20 xl:h-22 2xl:h-24' 
                : 'h-12'
            }`}
          />
        </div>

        {/* Connection Status & Controls */}
        <div className={`flex items-center ${
          isFullScreen ? 'space-x-4 pr-12' : 'space-x-4'
        }`}>
          {/* Dashboard ID */}
          {websocketId && (
            <div className="text-obn-lightblue text-sm font-mono">
              ID: {websocketId}
            </div>
          )}
          
          {/* Connection Status Indicator */}
          <div className={`flex items-center transition-all duration-300 ${
            isFullScreen ? 'space-x-3 ml-6' : 'space-x-2 ml-4 pr-5'
          }`}>
            <div 
              className={`rounded-full transition-all duration-300 ${
                isFullScreen 
                  ? 'w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6' 
                  : 'w-3 h-3'
              } ${
                connectionStatus === 'connected'
                  ? 'bg-green-400 shadow-lg shadow-green-400/50'
                  : connectionStatus === 'authenticating'
                  ? 'bg-yellow-400 shadow-lg shadow-yellow-400/50 animate-pulse'
                  : connectionStatus === 'error'
                  ? 'bg-red-400 shadow-lg shadow-red-400/50'
                  : 'bg-gray-400 shadow-lg shadow-gray-400/50 animate-pulse'
              }`}
              title={
                connectionStatus === 'connected' ? 'WebSocket Connected & Authenticated' :
                connectionStatus === 'authenticating' ? 'Authenticating WebSocket Connection' :
                connectionStatus === 'error' ? 'WebSocket Connection Error' :
                'WebSocket Disconnected'
              }
            />
            <span className={`text-white/80 transition-all duration-300 ${
              isFullScreen 
                ? 'text-base sm:text-lg md:text-xl font-medium' 
                : 'text-sm hidden sm:inline'
            }`}>
              {connectionStatus === 'connected' ? 'LIVE' :
               connectionStatus === 'authenticating' ? 'AUTHENTICATING' :
               connectionStatus === 'error' ? 'ERROR' : 'OFFLINE'}
            </span>
          </div>

          {/* Settings & Full Screen Controls */}
          {!isFullScreen && (
            <>
              {/* Grid Edit Toggle */}
              <button
                onClick={() => setIsGridEditing(!isGridEditing)}
                className={`px-3 py-2 rounded-lg transition-colors ${
                  isGridEditing
                    ? 'bg-obn-lightblue text-obn-blue'
                    : 'bg-white/10 hover:bg-white/20'
                }`}
                title={isGridEditing ? 'Save Layout' : 'Edit Layout'}
              >
                {isGridEditing ? 'Save Layout' : 'Edit Layout'}
              </button>

              {/* Settings Toggle */}
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                title="Dashboard Settings"
              >
                <Cog6ToothIcon className="h-6 w-6" />
              </button>

              {/* Full Screen Toggle */}
              <button
                onClick={toggleFullScreen}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-obn-blue hover:bg-obn-blue/90 transition-colors"
              >
                <ArrowsPointingOutIcon className="h-5 w-5" />
                <span>Full Screen</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="absolute top-20 right-6 z-20 w-80 bg-gray-800/95 backdrop-blur-sm rounded-lg shadow-xl border border-gray-700">
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Dashboard Settings</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Update Interval
                </label>
                <select className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white">
                  <option value="1">1 second</option>
                  <option value="5">5 seconds</option>
                  <option value="10">10 seconds</option>
                  <option value="30">30 seconds</option>
                </select>
              </div>
              

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-300">Auto-refresh</span>
                <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-obn-blue">
                  <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-6" />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-300">Debug Mode</span>
                <button 
                  onClick={() => setDebugMode(!debugMode)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    debugMode ? 'bg-obn-blue' : 'bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                    debugMode ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              
              {debugMode && (
                <div className="text-xs text-gray-400 bg-gray-700/50 p-2 rounded">
                  Debug mode enabled. WebSocket messages will appear in the top-right corner.
                </div>
              )}
            </div>

            <button
              onClick={() => setShowSettings(false)}
              className="mt-6 w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-md transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Main Dashboard Canvas - Grid-based Layout */}
      <div className={`relative pb-8 px-8 transition-all duration-300 mt-1 ${
        isFullScreen ? 'pt-24 sm:pt-26 md:pt-28 lg:pt-30 xl:pt-32 2xl:pt-36' : 'pt-24'
      }`} style={{ height: 'calc(100vh - 96px)' }}>
        <DashboardGrid
          columns={12}
          rows={8}
          gap={24}
          isEditing={isGridEditing}
          layout={gridLayout}
          onLayoutChange={setGridLayout}
        >
          {/* Time & Date Widget */}
          <DashboardWidget
            id="timeWidget"
            title="Time & Trends"
            isExpanded={expandedWidgetId === 'timeWidget'}
            onCollapse={handleCollapseWidget}
          >
            <div className="h-full bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="h-full flex flex-col relative">
                {/* Time Display */}
                <div className="text-center mb-4">
                  <div className="text-3xl font-mono font-bold text-obn-lightblue mb-1">
                    {formatTime(currentTime)}
                  </div>
                  <div className="text-sm text-obn-lightblue/80">
                    {formatDate(currentTime).split(',')[0]}
                  </div>
                </div>

                {/* Trend Line Chart */}
                <div className="flex-1 min-h-0">
                  <h4 className="text-xs font-semibold text-white/60 mb-2 text-center">Weekly Trend</h4>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                      <defs>
                        <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#cbe5f2" stopOpacity={0.8}/>
                          <stop offset="100%" stopColor="#175677" stopOpacity={0.1}/>
                        </linearGradient>
                      </defs>
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke="#cbe5f2"
                        strokeWidth={2}
                        fill="url(#areaGradient)"
                        dot={false}
                        activeDot={{ r: 3, stroke: '#cbe5f2', strokeWidth: 1, fill: '#175677' }}
                        animationDuration={0}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'rgba(23, 86, 119, 0.9)',
                          border: '1px solid rgba(203, 229, 242, 0.3)',
                          borderRadius: '4px',
                          color: '#cbe5f2',
                          fontSize: '11px'
                        }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </DashboardWidget>

          {/* Stats Grid - Newsletter Metrics */}
          <DashboardWidget
            id="statsGrid"
            title="Metrics"
            isExpanded={expandedWidgetId === 'statsGrid'}
            onCollapse={handleCollapseWidget}
          >
            <div className="h-full w-full p-4 flex flex-col">
              <div className="grid grid-cols-4 gap-5 flex-1">
                {newsletterMetrics.map((metric) => (
                  <div
                    key={metric.name}
                    className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-3 flex flex-col justify-center hover:bg-white/15 transition-all duration-300 group"
                  >
                    <div className="flex flex-col justify-center flex-1 pt-2">
                      <div
                        className="text-3xl font-bold mb-2 group-hover:scale-105 transition-transform duration-300 text-center"
                        style={{
                          color: metric.name === 'New Articles' ? '#ffffff' : metric.color,
                          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.2)'
                        }}
                      >
                        {metric.value}
                      </div>
                      <div className="text-xs text-white/80 font-medium text-center mb-3">
                        {metric.name}
                      </div>
                    </div>
                    <div className="flex items-end -mx-3">
                      <div className="w-full">
                        <ResponsiveContainer width="100%" height={40}>
                          <AreaChart data={metric.trend} margin={{ top: 0, right: -5, left: -5, bottom: 0 }}>
                            <defs>
                              <linearGradient id={`gradient-${metric.name.replace(/\s+/g, '').replace(/[()]/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#cbe5f2" stopOpacity={0.8}/>
                                <stop offset="100%" stopColor="#175677" stopOpacity={0.1}/>
                              </linearGradient>
                            </defs>
                            <Area
                              type="monotone"
                              dataKey="value"
                              stroke={metric.color}
                              strokeWidth={2}
                              fill={`url(#gradient-${metric.name.replace(/\s+/g, '').replace(/[()]/g, '')})`}
                              dot={false}
                              animationDuration={0}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </DashboardWidget>

          {/* Main Canvas - Weekly Content Chart */}
          <DashboardWidget
            id="mainCanvas"
            title="Content Activity"
            isExpanded={expandedWidgetId === 'mainCanvas'}
            onCollapse={handleCollapseWidget}
          >
            <div className="h-full bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 border-dashed flex flex-col overflow-hidden">
              {/* Custom Header */}
              <div className="flex flex-col items-center pt-6 pb-4">
                <h3 className="text-xl font-semibold text-white/80 mb-3">
                  Weekly Content Activity
                </h3>

                {/* Custom Legend */}
                <div className="flex items-center space-x-6 text-sm flex-wrap justify-center gap-y-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded bg-gradient-to-t from-obn-blue to-obn-lightblue"></div>
                    <span className="text-white/70">Articles (240)</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded bg-gradient-to-t from-obn-red to-red-300"></div>
                    <span className="text-white/70">Videos (104)</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded bg-gradient-to-t from-purple-400 to-purple-600"></div>
                    <span className="text-white/70">Podcasts (43)</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-0.5 bg-cyan-400 rounded"></div>
                    <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
                    <span className="text-white/70">Engagement</span>
                  </div>
                </div>
              </div>

              {/* Chart Container */}
              <div className="flex-1 px-4 pb-4 min-h-0">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={weeklyData}
                    margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="articlesGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#cbe5f2" stopOpacity={1}/>
                        <stop offset="100%" stopColor="#175677" stopOpacity={1}/>
                      </linearGradient>
                      <linearGradient id="videosGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#fca5a5" stopOpacity={1}/>
                        <stop offset="100%" stopColor="#d60a3c" stopOpacity={1}/>
                      </linearGradient>
                      <linearGradient id="podcastsGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#c4b5fd" stopOpacity={1}/>
                        <stop offset="100%" stopColor="#8b5cf6" stopOpacity={1}/>
                      </linearGradient>
                    </defs>

                    <CartesianGrid
                      strokeDasharray="2 2"
                      stroke="rgba(255,255,255,0.15)"
                      horizontal={true}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="day"
                      axisLine={{ stroke: 'rgba(255,255,255,0.3)', strokeWidth: 1 }}
                      tickLine={{ stroke: 'rgba(255,255,255,0.3)', strokeWidth: 1 }}
                      tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 14 }}
                    />
                    <YAxis
                      axisLine={{ stroke: 'rgba(255,255,255,0.3)', strokeWidth: 1 }}
                      tickLine={{ stroke: 'rgba(255,255,255,0.3)', strokeWidth: 1 }}
                      tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }}
                      domain={[0, 100]}
                      tickCount={6}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(23, 86, 119, 0.95)',
                        border: '1px solid rgba(203, 229, 242, 0.3)',
                        borderRadius: '8px',
                        color: '#cbe5f2',
                        backdropFilter: 'blur(8px)',
                        transition: 'opacity 200ms ease-in-out'
                      }}
                      labelStyle={{ color: '#cbe5f2' }}
                      wrapperStyle={{ transition: 'none' }}
                    />
                    <Bar
                      dataKey="articles"
                      stackId="content"
                      fill="url(#articlesGradient)"
                      animationDuration={0}
                      isAnimationActive={false}
                    />
                    <Bar
                      dataKey="videos"
                      stackId="content"
                      fill="url(#videosGradient)"
                      animationDuration={0}
                      isAnimationActive={false}
                    />
                    <Bar
                      dataKey="podcasts"
                      stackId="content"
                      fill="url(#podcastsGradient)"
                      radius={[6, 6, 0, 0]}
                      animationDuration={0}
                      isAnimationActive={false}
                    />

                    {/* Engagement Line Overlay */}
                    <Line
                      type="monotone"
                      dataKey="engagement"
                      stroke="#22d3ee"
                      strokeWidth={3}
                      dot={{ fill: '#22d3ee', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#22d3ee', strokeWidth: 2, fill: '#0891b2' }}
                      animationDuration={0}
                      isAnimationActive={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </DashboardWidget>

          {/* Side Panel - System Status */}
          <DashboardWidget
            id="sidePanel"
            title="System Status"
            isExpanded={expandedWidgetId === 'sidePanel'}
            onCollapse={handleCollapseWidget}
          >
            <div className="h-full bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="h-full overflow-y-auto overflow-x-hidden">
                <h3 className="text-xl font-semibold mb-6 text-center">System Status</h3>

                <div className="space-y-4">
                  {[
                    { label: 'API Status', status: 'Online', color: 'bg-green-500' },
                    { label: 'Database', status: 'Connected', color: 'bg-green-500' },
                    { label: 'WebSocket', status: 'Active', color: 'bg-obn-blue' },
                    { label: 'Background Jobs', status: 'Running', color: 'bg-obn-lightblue' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <span className="text-sm text-gray-300">{item.label}</span>
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${item.color}`} />
                        <span className="text-sm text-white">{item.status}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Activity Feed */}
                <div className="mt-8">
                  <h4 className="text-lg font-medium mb-4">Recent Activity</h4>
                  <div className="space-y-3">
                    {[
                      'New item processed',
                      'Issue #156 published',
                      'Author profile updated',
                      'System backup completed'
                    ].map((activity, index) => (
                      <div key={index} className="flex items-center space-x-3 text-sm">
                        <div className="w-1 h-1 bg-obn-lightblue rounded-full" />
                        <span className="text-gray-300">{activity}</span>
                        <span className="text-gray-500 ml-auto">
                          {Math.floor(Math.random() * 30) + 1}m ago
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </DashboardWidget>
        </DashboardGrid>
      </div>

      {/* Debug Messages Window */}
      {debugMode && (
        <div className="fixed top-4 right-4 z-50 w-96 max-h-96 bg-gray-900/95 backdrop-blur-sm rounded-lg shadow-xl border border-gray-700 overflow-hidden">
          <div className="p-3 bg-gray-800 border-b border-gray-700 flex items-center justify-between">
            <h4 className="text-sm font-medium text-white">WebSocket Debug</h4>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-400">{debugMessages.length} messages</span>
              <button
                onClick={() => setDebugMessages([])}
                className="text-xs px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded transition-colors text-gray-300"
              >
                Clear
              </button>
              <button
                onClick={() => setDebugMode(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                ×
              </button>
            </div>
          </div>
          <div className="overflow-y-auto max-h-80 p-2 space-y-2">
            {debugMessages.length === 0 ? (
              <div className="text-center text-gray-500 text-sm py-4">
                No messages yet. Send a command from the Commands page to see them here.
              </div>
            ) : (
              debugMessages.map((msg) => (
                <div key={msg.id} className="bg-gray-800/50 rounded p-2 text-xs">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-obn-lightblue font-medium">
                      {msg.data.type || 'unknown'}
                    </span>
                    <span className="text-gray-500">
                      {new Date(msg.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <pre className="text-gray-300 whitespace-pre-wrap break-words text-xs">
                    {JSON.stringify(msg.data, null, 2)}
                  </pre>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Audio Settings Popup */}
      {showAudioSettings && (
        <div className="fixed bottom-20 right-6 z-50" style={{width: '280px'}}>
          <div className="bg-black/90 backdrop-blur-sm border border-gray-600 rounded-xl shadow-xl">
            <div className="p-3 space-y-4">
              {/* Voice Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Voice (for all audio responses)
                </label>
                <select
                  value={`${voicePreference.id}-${voicePreference.engine}`}
                  onChange={(e) => {
                    const [voiceId, engine] = e.target.value.split(/-(.+)/); // Split on first hyphen only
                    if (debugMode) console.log('🔍 Voice changed - raw value:', e.target.value, 'parsed:', { voiceId, engine });
                    setVoicePreference(prev => ({ ...prev, id: voiceId, engine }));
                    setCurrentVoice(`${voiceId}-${engine}`);
                  }}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                >
                  <optgroup label="Generative (Most Natural)">
                    <option value="Danielle-generative">Danielle (US, Female)</option>
                    <option value="Joanna-generative">Joanna (US, Female)</option>
                    <option value="Matthew-generative">Matthew (US, Male)</option>
                    <option value="Amy-generative">Amy (UK, Female)</option>
                    <option value="Niamh-generative">Niamh (Irish, Female)</option>
                    <option value="Ayanda-generative">Ayanda (South African, Female)</option>
                  </optgroup>
                  <optgroup label="Long-Form (For longer content)">
                    <option value="Danielle-long-form">Danielle (US, Female)</option>
                    <option value="Amy-long-form">Amy (UK, Female)</option>
                    <option value="Emma-long-form">Emma (UK, Female)</option>
                    <option value="Brian-long-form">Brian (UK, Male)</option>
                    <option value="Olivia-long-form">Olivia (Australian, Female)</option>
                  </optgroup>
                  <optgroup label="Neural - US English">
                    <option value="Danielle-neural">Danielle (Female)</option>
                    <option value="Joanna-neural">Joanna (Female)</option>
                    <option value="Kendra-neural">Kendra (Female)</option>
                    <option value="Kimberly-neural">Kimberly (Female)</option>
                    <option value="Salli-neural">Salli (Female)</option>
                    <option value="Ruth-neural">Ruth (Female)</option>
                    <option value="Matthew-neural">Matthew (Male)</option>
                    <option value="Joey-neural">Joey (Male)</option>
                    <option value="Justin-neural">Justin (Male)</option>
                    <option value="Gregory-neural">Gregory (Male)</option>
                    <option value="Stephen-neural">Stephen (Male)</option>
                  </optgroup>
                  <optgroup label="Neural - UK English">
                    <option value="Amy-neural">Amy (Female)</option>
                    <option value="Emma-neural">Emma (Female)</option>
                    <option value="Brian-neural">Brian (Male)</option>
                  </optgroup>
                  <optgroup label="Neural - Australian">
                    <option value="Nicole-neural">Nicole (Female)</option>
                    <option value="Olivia-neural">Olivia (Female)</option>
                    <option value="Russell-neural">Russell (Male)</option>
                  </optgroup>
                  <optgroup label="Neural - Other Accents">
                    <option value="Aditi-neural">Aditi (Indian, Female)</option>
                    <option value="Raveena-neural">Raveena (Indian, Female)</option>
                    <option value="Kajal-neural">Kajal (Indian, Female)</option>
                    <option value="Niamh-neural">Niamh (Irish, Female)</option>
                    <option value="Aria-neural">Aria (New Zealand, Female)</option>
                    <option value="Ayanda-neural">Ayanda (South African, Female)</option>
                  </optgroup>
                  <optgroup label="Standard - US English">
                    <option value="Danielle-standard">Danielle (Female)</option>
                    <option value="Joanna-standard">Joanna (Female)</option>
                    <option value="Kendra-standard">Kendra (Female)</option>
                    <option value="Kimberly-standard">Kimberly (Female)</option>
                    <option value="Salli-standard">Salli (Female)</option>
                    <option value="Ruth-standard">Ruth (Female)</option>
                    <option value="Ivy-standard">Ivy (Female)</option>
                    <option value="Matthew-standard">Matthew (Male)</option>
                    <option value="Joey-standard">Joey (Male)</option>
                    <option value="Justin-standard">Justin (Male)</option>
                    <option value="Gregory-standard">Gregory (Male)</option>
                    <option value="Stephen-standard">Stephen (Male)</option>
                    <option value="Kevin-standard">Kevin (Male)</option>
                    <option value="Patrick-standard">Patrick (Male)</option>
                  </optgroup>
                  <optgroup label="Standard - UK English">
                    <option value="Amy-standard">Amy (Female)</option>
                    <option value="Emma-standard">Emma (Female)</option>
                    <option value="Brian-standard">Brian (Male)</option>
                    <option value="Arthur-standard">Arthur (Male)</option>
                  </optgroup>
                  <optgroup label="Standard - Other Accents">
                    <option value="Nicole-standard">Nicole (Australian, Female)</option>
                    <option value="Russell-standard">Russell (Australian, Male)</option>
                    <option value="Raveena-standard">Raveena (Indian, Female)</option>
                    <option value="Niamh-standard">Niamh (Irish, Female)</option>
                    <option value="Aria-standard">Aria (New Zealand, Female)</option>
                    <option value="Ayanda-standard">Ayanda (South African, Female)</option>
                  </optgroup>
                </select>
              </div>

              {/* Speed Control */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Speed: {rate.toFixed(1)}x
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="2.0"
                  step="0.1"
                  value={rate}
                  onChange={(e) => {
                    const newRate = parseFloat(e.target.value);
                    setRate(newRate);
                    // Update voice preference speed to use actual rate value
                    setVoicePreference(prev => ({ ...prev, speed: newRate.toString() }));
                  }}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                />
              </div>

              {/* Volume Control */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Volume: {Math.round(volume * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={volume}
                  onChange={(e) => setVolume(parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                />
              </div>

              {/* Test Voice Button */}
              <button
                onClick={() => {
                  if (debugMode) console.log('🔍 Test Voice clicked - voicePreference:', JSON.stringify(voicePreference, null, 2));
                  if (isConnected) {
                    mutedAwareStreamResponse(sendMessage, "Testing audio settings with current voice configuration.", {
                      voice: voicePreference
                    });
                  } else {
                    mutedAwareSpeakText("Testing audio settings.", {
                      rate: rate,
                      volume: volume
                    });
                  }
                }}
                disabled={isSpeaking && !isConnected}
                className="w-full bg-obn-blue hover:bg-obn-blue/90 disabled:opacity-50 disabled:cursor-not-allowed text-white py-2 px-4 rounded flex items-center justify-center space-x-2"
              >
                <PlayIcon className="h-4 w-4" />
                <span>
                  {isSpeaking && !isConnected ? 'Speaking...' : 'Test Voice'}
                </span>
              </button>

              {/* Mute Button */}
              <button
                onClick={handleAudioMute}
                className="w-full px-2 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm transition-colors"
                title="Mute audio"
              >
                Mute
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat Popup */}
      {showChat && (
        <div className="fixed bottom-20 right-6 z-50" style={{width: '280px'}}>
          <div className="bg-black/90 backdrop-blur-sm border border-gray-600 rounded-xl shadow-xl">
            <div className="p-3">
              <form onSubmit={handleChatSubmit}>
                <textarea
                  ref={chatTextareaRef}
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  onKeyDown={handleChatKeyDown}
                  placeholder="How can I help?"
                  autoComplete="off"
                  data-1p-ignore
                  data-lpignore="true"
                  data-form-type="other"
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-obn-blue focus:border-transparent"
                  rows={3}
                  style={{
                    minHeight: '40px',
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}
                />
                <div className="flex justify-between items-center mt-2">
                  <span className="text-xs text-gray-400">
                    Shift+Enter for new line
                  </span>
                  <div className="flex items-center space-x-2">
                    {isChatProcessing && (
                      <div className="animate-spin w-4 h-4 border-2 border-obn-blue border-t-transparent rounded-full"></div>
                    )}
                    <button
                      type="submit"
                      disabled={!chatMessage.trim() || isChatProcessing}
                      className="px-3 py-1 bg-obn-blue hover:bg-obn-blue/90 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded text-sm transition-colors"
                    >
                      Send
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Voice Input Controls */}
      {isVoiceSupported && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end space-y-3">
          {/* Voice Command Result Display */}
          {(voiceCommandResult || isProcessingCommand) && (
            <div style={{width: '280px'}} className={`p-3 rounded-lg shadow-lg border backdrop-blur-sm ${
              voiceCommandResult?.error
                ? 'bg-red-900/90 border-red-500/50 text-red-100'
                : 'bg-obn-blue/90 border-obn-lightblue/50 text-white'
            }`}>
              {isProcessingCommand ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full"></div>
                  <span className="text-sm">Processing command...</span>
                </div>
              ) : voiceCommandResult?.error ? (
                <div>
                  <div className="font-medium text-sm">Command Error</div>
                  <div className="text-xs opacity-90 mt-1">{voiceCommandResult.error}</div>
                </div>
              ) : voiceCommandResult ? (
                <div>
                  <div className="font-medium text-sm">
                    {executionResult?.message || voiceCommandResult.explanation || 'Command processed'}
                  </div>
                  <div className="text-xs opacity-90 mt-1">
                    Intent: {voiceCommandResult.intent?.replace(/_/g, ' ')?.toUpperCase()}
                    {voiceCommandResult.confidence && (
                      <span className="ml-2">
                        ({Math.round(voiceCommandResult.confidence * 100)}% confident)
                      </span>
                    )}
                  </div>
                  {executionResult && (
                    <div className={`text-xs mt-1 ${executionResult.success ? 'text-green-300' : 'text-red-300'}`}>
                      {executionResult.success ? '✓ Executed' : `✗ ${executionResult.error}`}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}

          {/* Voice Transcription Display */}
          {(displayText || isListening) && (
            <div style={{width: '280px'}} className="p-3 rounded-lg shadow-lg border backdrop-blur-sm bg-black/80 border-gray-600 text-white">
              {/* Wake word status indicator */}
              {wakeWord && (
                <div className="text-xs mb-2 flex items-center space-x-2">
                  {isWaitingForWakeWord ? (
                    <div className="flex items-center space-x-1 text-yellow-300">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                      <span>Waiting for wake word: "{wakeWord}"</span>
                    </div>
                  ) : wakeWordActiveUntil && Date.now() < wakeWordActiveUntil ? (
                    <div className="flex items-center space-x-1 text-green-300">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <span>Wake word active: "{wakeWord}"</span>
                      <span className="text-xs opacity-70">
                        ({Math.ceil((wakeWordActiveUntil - Date.now()) / 1000)}s)
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1 text-blue-300">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      <span>Wake word set: "{wakeWord}"</span>
                    </div>
                  )}
                </div>
              )}

              <div className="text-sm break-words overflow-wrap-anywhere">
                {displayText || (wakeWord && isWaitingForWakeWord ? `Say "${wakeWord}" to begin...` : 'Say something...')}
                {isWaitingForMoreSpeech && displayText && (
                  <svg className="inline animate-spin h-3 w-3 text-current ml-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
              </div>
            </div>
          )}


          <div className="flex space-x-3">
            {/* Audio Control Button - Always visible */}
            <button
              onClick={handleAudioButtonClick}
              className={`w-12 h-12 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center cursor-pointer transform hover:scale-110 ${
                isAudioMuted
                  ? 'bg-red-600 hover:bg-red-500'
                  : showAudioSettings
                    ? 'bg-green-600 hover:bg-green-500'
                    : 'bg-gray-600 hover:bg-gray-500'
              }`}
              title={isAudioMuted ? "Unmute audio" : showAudioSettings ? "Close audio settings" : "Open audio settings"}
            >
              {isAudioMuted ? (
                <SpeakerXMarkIcon className="h-5 w-5 text-white" />
              ) : (
                <SpeakerWaveIcon className="h-5 w-5 text-white" />
              )}
            </button>

            {/* TTS Control Button */}
            {isTTSSupported && isSpeaking && (
              <button
                onClick={() => {
                  // Close popups when stopping TTS
                  setShowAudioSettings(false);
                  setShowChat(false);
                              stopSpeaking();
                }}
                className={`w-12 h-12 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center animate-pulse cursor-pointer transform hover:scale-110 ${
                  usingPolly
                    ? 'bg-purple-500 hover:bg-purple-400'
                    : 'bg-orange-500 hover:bg-orange-400'
                }`}
                title={`Stop voice output (${ttsProvider === 'polly' ? 'Amazon Polly' : 'Web Speech API'})`}
              >
                <SpeakerWaveIcon className="h-5 w-5 text-white" />
              </button>
            )}

            {/* Voice Input Button */}
            <button
              onClick={handleVoiceButtonClick}
              disabled={!isVoiceSupported}
              className={`w-12 h-12 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center transform group ${
                delayedIsListening || isProcessing
                  ? 'bg-red-500 hover:bg-red-400 animate-pulse'
                  : 'bg-obn-blue hover:bg-obn-blue/60'
              } ${!isVoiceSupported ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-110'}`}
              title={
                !isVoiceSupported
                  ? 'Voice input not supported'
                  : isVoiceActive
                    ? 'Stop continuous voice mode'
                    : 'Start continuous voice mode'
              }
            >
              {delayedIsListening || isProcessing ? (
                delayedIsListening ? (
                  <VoiceWaveform
                    isListening={isListening}
                    hasInterimResults={isActivelySpeaking}
                    className="text-white"
                  />
                ) : (
                  <XMarkIcon className="h-5 w-5 text-white" />
                )
              ) : (
                <MicrophoneIcon className="h-5 w-5 text-white group-hover:text-obn-lightblue transition-colors duration-300" />
              )}
            </button>


            {/* Chat Button */}
            <button
              onClick={() => {
                setShowChat(!showChat);
                // Close other popups when opening chat
                if (!showChat) {
                  setShowAudioSettings(false);
                              // Focus on textarea after state update
                  setTimeout(() => {
                    chatTextareaRef.current?.focus();
                  }, 0);
                }
              }}
              className="w-12 h-12 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center bg-obn-lightblue hover:bg-white cursor-pointer transform hover:scale-110"
              title="Open chat"
            >
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-obn-blue" />
            </button>
          </div>

        </div>
      )}

      {/* Full Screen Instructions */}
      {!isFullScreen && (
        <div className="absolute bottom-4 left-4">
          <div className="bg-black/40 backdrop-blur-sm rounded-lg px-4 py-2 text-sm text-white/80">
            Press F11 or click Full Screen for monitor display
          </div>
        </div>
      )}
    </div>
  );
}

export default LiveDashboardPage;
